import { ActionTree } from 'vuex'
import {
  createSepaPaymentMethod,
  fetchPaymentMethods,
  removePaymentMethod,
} from '@/sdk/clients/UserClient'
import logger from '@/sdk/logger'
import { PaymentMethodsState, Mutations } from './types'
import { RootState } from '../types'
import PaymentMethod from './types/PaymentMethod'

const actions: ActionTree<PaymentMethodsState, RootState> = {
  async createSepaPaymentMethod(
    _,
    {
      accountHolder,
      street,
      postcode,
      city,
      iban,
      mandateReturnUrl,
    }: {
      accountHolder: string
      street: string
      postcode: string
      city: string
      iban: string
      mandateReturnUrl: string
    },
  ) {
    const payload = {
      iban,
      account_holder: {
        full_name: accountHolder,
        street,
        postal_code: postcode,
        city,
      },
      mandate_return_url: mandateReturnUrl,
    }

    await createSepaPaymentMethod(payload)
  },

  async fetchPaymentMethods({ commit }) {
    const paymentMethods = await fetchPaymentMethods()

    commit(Mutations.SET_PAYMENT_METHODS, paymentMethods)

    return paymentMethods
  },

  async fetchSepaMandateUrl({ dispatch }) {
    const maxRetryAttempts = 5
    const nextAttemptInMs = 2000
    let sepaPaymentMethod: PaymentMethod
    let maxAttemptsReached = false

    for (const attempt of Array.from({ length: maxRetryAttempts }).map(
      (_, i) => i,
    )) {
      if (sepaPaymentMethod?.data?.mandate?.confirmationUrl) {
        break
      }

      if (attempt === maxRetryAttempts - 1) {
        maxAttemptsReached = true

        break
      }

      // eslint-disable-next-line no-await-in-loop
      const paymentMethods = await dispatch('fetchPaymentMethods')
      sepaPaymentMethod = paymentMethods.find(
        paymentMethod => paymentMethod.type === 'sepa_direct_debit',
      )

      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, nextAttemptInMs))
    }

    if (maxAttemptsReached) {
      const errorText = `Max ${maxRetryAttempts} attempts reached while fetching the mandate url`
      const error = new Error(errorText)

      logger.error(errorText, {
        error,
        tags: ['dashboard', 'paymentMethods', 'fetchSepaMandateUrlPolling'],
        additionalData: {
          paymentMethodId: sepaPaymentMethod?.paymentMethodId,
        },
      })

      throw error
    }
  },

  async fetchSepaActivationStatus({ dispatch }) {
    const maxRetryAttempts = 5
    const nextAttemptInMs = 2000
    let sepaPaymentMethod: PaymentMethod
    let maxAttemptsReached = false

    for (const attempt of Array.from({ length: maxRetryAttempts }).map(
      (_, i) => i,
    )) {
      if (sepaPaymentMethod?.activationStatus === 'activated') {
        break
      }

      if (attempt === maxRetryAttempts - 1) {
        maxAttemptsReached = true

        break
      }

      // eslint-disable-next-line no-await-in-loop
      const paymentMethods = await dispatch('fetchPaymentMethods')
      sepaPaymentMethod = paymentMethods.find(
        paymentMethod => paymentMethod.type === 'sepa_direct_debit',
      )

      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, nextAttemptInMs))
    }

    if (maxAttemptsReached) {
      const errorText = `Max ${maxRetryAttempts} attempts reached while fetching the mandate status`
      const error = new Error(errorText)

      logger.error(errorText, {
        error,
        tags: [
          'dashboard',
          'paymentMethods',
          'fetchSepaActivationStatusPolling',
        ],
        additionalData: {
          paymentMethodId: sepaPaymentMethod?.paymentMethodId,
        },
      })

      throw error
    }
  },

  async removePaymentMethod(_, paymentMethodId: string) {
    return removePaymentMethod(paymentMethodId)
  },
}

export default actions
